import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  harnessIcon,
  harnessLabel,
  modelFundingLabel,
} from "@/constants/harnesses";
import { cn } from "@/utils";
import { HarnessCapability, HarnessId } from "@cohesive/api-client";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { HarnessControl, HarnessModelOption } from "../hooks/useHarnessControl";

const PILL_CLASS =
  "h-7 rounded-full px-2 gap-1 text-xs font-medium transition-colors shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent";

const MENU_CLASS = "w-72 p-2 rounded-[14px] border";

const ROW_CLASS =
  "w-full flex items-center gap-3 min-h-11 px-4 py-2 rounded-lg text-left transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-50";

function HarnessIcon({ harness }: { harness: string }) {
  const Icon = harnessIcon(harness);
  return Icon ? <Icon className="size-4 shrink-0" /> : null;
}

/**
 * Harness ("what this chat runs on") and, where the harness offers them, its model.
 *
 * The harness is spent by the chat's first message, so this is a picker until then — on a
 * composer that has yet to mint a session, and inside a chat with no messages yet; after the
 * first message the same slot states what the chat runs on and cannot be changed. The model
 * stays switchable for the life of the chat, but only
 * within the selected harness's own catalog — the vocabularies don't cross — and, on a harness
 * whose sandbox resolved its credentials once, only within the provider the chat started on.
 *
 * Availability and the sentence explaining an unavailable harness are the server's: a member
 * with no Anthropic credential sees Claude dimmed with the reason to connect one, rather
 * than a guess assembled here.
 *
 * Shape follows the reasoning-effort pill next door — trigger carries the current value, the
 * fly-up menu carries the choice — so the two dials read as one control cluster.
 */
export function HarnessSelectButton({ control }: { control: HarnessControl }) {
  const [harnessOpen, setHarnessOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);

  const { locked, harness, capability, capabilities, model, models } = control;
  const label = harnessLabel(harness, capability?.label);
  const modelLabel = models.find((m) => m.id === model)?.label ?? model;

  return (
    <div className="flex items-center gap-1 shrink-0">
      {locked ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <div
              // Focusable so the tooltip — the only place the chat says why its harness
              // can't be changed — is reachable without a pointer.
              tabIndex={0}
              className={cn(
                PILL_CLASS,
                "inline-flex items-center hover:bg-transparent hover:text-muted-foreground cursor-default",
              )}
              aria-label={`Runs on ${label}`}
            >
              <HarnessIcon harness={harness} />
              <span className="truncate">{label}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Runs on {label} — fixed when this chat started
          </TooltipContent>
        </Tooltip>
      ) : (
        <Popover open={harnessOpen} onOpenChange={setHarnessOpen}>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className={PILL_CLASS}
                  aria-label={`Chat runs on: ${label}`}
                >
                  <HarnessIcon harness={harness} />
                  <span className="truncate">{label}</span>
                  <ChevronDown className="h-3 w-3 shrink-0" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Choose what this chat runs on
            </TooltipContent>
          </Tooltip>

          <PopoverContent
            side="top"
            align="start"
            sideOffset={8}
            className={MENU_CLASS}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            {capabilities.map((option) => (
              <HarnessRow
                key={option.id}
                option={option}
                selected={option.id === harness}
                onSelect={() => {
                  setHarnessOpen(false);
                  control.setHarness(option.id as HarnessId);
                }}
              />
            ))}
          </PopoverContent>
        </Popover>
      )}

      {/* Cohesive's dial is the reasoning effort tier, not a model, so it offers none — and a
          harness the capabilities call can no longer describe (the flag withdrawn under a
          stamped chat) states its model rather than offering a menu of one. */}
      {models.length > 0 ? (
        <Popover open={modelOpen} onOpenChange={setModelOpen}>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className={PILL_CLASS}
                  aria-label={`Model: ${modelLabel}`}
                >
                  <span className="truncate">{modelLabel}</span>
                  <ChevronDown className="h-3 w-3 shrink-0" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">Set the model</TooltipContent>
          </Tooltip>

          <PopoverContent
            side="top"
            align="start"
            sideOffset={8}
            className={MENU_CLASS}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            {models.map((option) => (
              <ModelRow
                key={option.id}
                option={option}
                selected={option.id === model}
                onSelect={() => {
                  setModelOpen(false);
                  control.setModel(option.id);
                }}
              />
            ))}
            {/* Stated once under the menu rather than under each dimmed row: it is one
                rule about the chat, not a fact about any particular model. */}
            {control.modelSwitchLocked && (
              <p className="px-4 pt-2 pb-1 text-xs text-muted-foreground leading-snug">
                {label} chats are locked to the provider they started with.
                Start a new chat to use a different provider.
              </p>
            )}
          </PopoverContent>
        </Popover>
      ) : (
        locked &&
        model && (
          <span className={cn(PILL_CLASS, "inline-flex items-center truncate")}>
            {modelLabel}
          </span>
        )
      )}
    </div>
  );
}

function ModelRow({
  option,
  selected,
  onSelect,
}: {
  option: HarnessModelOption;
  selected: boolean;
  onSelect: () => void;
}) {
  const funding = modelFundingLabel(option.funding);
  return (
    <button
      disabled={option.disabled}
      onClick={onSelect}
      className={cn(ROW_CLASS, selected && "bg-accent/60")}
    >
      <div className="flex-1 min-w-0 text-sm">{option.label}</div>
      {funding && (
        <span className="shrink-0 text-xs text-muted-foreground">
          {funding}
        </span>
      )}
      {selected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
    </button>
  );
}

function HarnessRow({
  option,
  selected,
  onSelect,
}: {
  option: HarnessCapability;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      disabled={!option.available}
      onClick={onSelect}
      className={cn(ROW_CLASS, selected && "bg-accent/60")}
    >
      <HarnessIcon harness={option.id} />
      <div className="flex-1 min-w-0">
        <div className="text-sm">{harnessLabel(option.id, option.label)}</div>
        {!option.available && option.unavailable_reason && (
          <div className="text-xs text-muted-foreground leading-snug">
            {option.unavailable_reason}
          </div>
        )}
      </div>
      {selected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
    </button>
  );
}
